import { AfterViewInit, Component, Inject, Injector, OnInit, Optional, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { AgentGroupDialogComponent } from 'src/app/modals/agent-group-dialog/agent-group-dialog.component';
import { GroupToAgentDialogComponent } from 'src/app/modals/group-to-agent-dialog/group-to-agent-dialog.component';
import { AgentGroup } from 'src/app/models/agentGroup.model';
import { Lookup } from 'src/app/models/lookup.model';
import { AuthService } from 'src/app/services';
import { AgentGroupService } from 'src/app/services/agentGroup.service';
import { AgentGroupOrgLevelService } from 'src/app/services/agent-group-org-level.service';
import { CommonService } from 'src/app/services/common.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { ToastService } from 'src/app/services/toast.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { segsRouteConfig } from 'src/app/shared/constants/segsRouteConfig.constants';
import { DeleteDialog, IDeleteDialogData } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { TranslateService } from '@ngx-translate/core';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { ISearchFieldData } from 'src/app/shared/grid/baseGrid.component';
import { AgentsListComponent } from '../agents-list/agents-list.component';
import { AgentGroupDetailsPopupComponent } from 'src/app/common-components/agent-group-details-popup/agent-group-details-popup.component';
import { FlyoutContainerComponent } from 'src/app/shared/flyout/flyout-container/flyout-container.component';
import { AgentGroupOrgLevel } from 'src/app/models/agentGroupOrglevel.model';
import { GridPermissions, gridToPermissionFlags } from 'src/app/shared/grid/core-grid/gridPermissions.enum';
import { ChildColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';

@Component({
  standalone: false,
  selector: 'app-agent-group-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss', './agent-group-list.component.scss']
})
export class AgentGroupListComponent extends CoreGridComponent<AgentGroup> implements OnInit, AfterViewInit {
  @ViewChild(FlyoutContainerComponent) flyout!: FlyoutContainerComponent;

  organizationLevels: Lookup[] = [];
  isNewGroup = false;
  private showArchivedOnly = false;

  constructor(
    protected agentGroupService: AgentGroupService,
    toastService: ToastService,
    commonService: CommonService,
    dialog: MatDialog,
    authService: AuthService,
    private organizationLevelService: OrganizationLevelService,
    private agentGroupOrgLevelService: AgentGroupOrgLevelService,
    gridViewService: GridViewService,
    private translationService: TranslateService,
    gridSettingsService: GridSettingsService,
    protected injector: Injector,
    @Optional() @Inject(MAT_DIALOG_DATA) public searchFieldData: ISearchFieldData
  ) {
    super(toastService,
      dialog,
      commonService,
      authService,
      gridViewService,
      gridSettingsService,
    );
    this.gridService = agentGroupService;
  }

  ngOnInit(): void {
    const searchDialogRef = this.injector.get<MatDialogRef<AgentsListComponent> | null>(MatDialogRef, null);
    if (searchDialogRef) {
      this.searchDialogRef = searchDialogRef;
    }
    super.ngOnInit();
    this.organizationLevelService.getAll().subscribe(orgs => this.organizationLevels = orgs);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  protected get objectId(): string {
    return RolePermissionObjects.AgentGroup;
  }

  // Add To My Site and Remove write GLOBAL_AgentGroup_OrgLevel, and PermissionCheckInterceptor
  // authorizes each independently against that object (not GLOBAL_AgentGroup), so gate them on
  // the join entity's own Create/Delete flag rather than the parent grid's canCreate/canDelete.
  protected get canAddToMySite(): boolean {
    return this.permissionService.hasPermissionFlag(RolePermissionObjects.AgentGroupOrgLevel, gridToPermissionFlags[GridPermissions.Create]);
  }

  protected get canRemoveOrgLevel(): boolean {
    return this.permissionService.hasPermissionFlag(RolePermissionObjects.AgentGroupOrgLevel, gridToPermissionFlags[GridPermissions.Delete]);
  }

  protected override get isGridViewEnabled(): boolean { return true; }

  override initializeProperties(): void {
    this.addEntityTitle = 'Agent Groups';
    this.searchPlaceholder = 'Search Agent Groups';
    this.deleteActionLabel = 'Archive';
    this.deleteActionIcon = 'archive';

    this.additionalButtons = [
      {
        title: this.showArchivedOnly ? 'Show Active Agent Groups' : 'Show Archived Agent Groups',
        readonly: false,
        onClick: () => this.toggleArchivedView()
      }
    ];
  }

  override initializeColumns(): void {
    this.columnConfigs = [
      {
        name: nameOf<AgentGroup>(x => x.name), 
        displayName: 'Agent Group Name', 
        cssClass: () => 'left-aligned cursor-pointer', 
        headerCssClass: () => 'left-aligned',
        onClick: (row: AgentGroup) => {
          if (!this.showArchivedOnly) {
            this.showAgentGroupDetails(row);
          }
        }
      },
      { name: nameOf<AgentGroup>(x => x.casId), cellTemplate: (row: AgentGroup) => row.casId || '_', displayName: 'Code' },
      { name: nameOf<AgentGroup>(x => x.type), displayName: 'Type' },
      {
        name: nameOf<AgentGroup>(x => x.organizationLevelId),
        displayName: 'Visibility',
        cellTemplate: (row: AgentGroup) => this.organizationLevels?.find(org => org.id === row.organizationLevelId)?.name || 'N/A'
      },
      {
        name: nameOf<AgentGroup>(x => x.agentIds), displayName: '', isDisplayNameConfigurable: false,
        cellTemplate: () => this.translationService.instant('Add Agents'), cssClass: () => 'add-agents-link',
        onClick: (row: AgentGroup) => { this.addAgentsToGroup(row) },
      },
      {
        name: nameOf<AgentGroup>(x => x.addedToMySite), displayName: '', isDisplayNameConfigurable: false,
        cssClass: () => this.showArchivedOnly ? 'd-none' : '',
        headerCssClass: () => this.showArchivedOnly ? 'd-none' : '',
        cellTemplate: (row: AgentGroup) => '',
        children: (row: AgentGroup) => {
          if (!row?.addedToMySite) {
            if (!this.canAddToMySite) {
              return [];
            }
            return [
              {
                onClick: agentGroup => this.addAgentGroupToSite(agentGroup.id),
                cssClass: () => 'agent-btn main-btn',
                cellTemplate: () => this.translationService.instant('Add Agent Group to Site'),
              },
            ]
          }
          // The association is hierarchy-wide (AddedToMySiteGridService) but Remove deletes at
          // exactly the selected org level. When it's held at a descendant only, Remove would
          // silently no-op, so route to the Agent Group's details view instead of offering Remove.
          if (!row.addedAtSelectedOrgLevel) {
            return [
              {
                onClick: agentGroup => this.showAgentGroupDetails(agentGroup),
                cssClass: () => 'main-btn agent-added-btn agent-added-btn-linked',
                cellTemplate: () => this.translationService.instant('Added'),
              },
            ]
          }

          const children: ChildColumnConfig<AgentGroup>[] = [
            {
              cssClass: () => 'main-btn agent-added-btn',
              cellTemplate: () => this.translationService.instant('Added'),
              readonly: () => true
            },
          ];
          if (this.canRemoveOrgLevel) {
            children.push({
              onClick: agentGroup => this.openRemoveAgentGroupFromSiteDialog(agentGroup.id),
              cssClass: () => 'agent-btn agent-remove-btn main-btn ml-2',
              cellTemplate: () => this.translationService.instant('Remove'),
            });
          }
          return children;
        }
      },
      { name: 'action', displayName: 'Actions' }
    ];
  }

  get hasOrgFilter(): boolean {
    return true;
  }

  override objectFilterMap(propertyName: string): any | undefined {
    switch (propertyName) {
      case nameOf<AgentGroup>(x => x.organizationLevelId): return this.organizationLevelService;
      default: return undefined;
    }
  }

  override initializeTabs() {
    return segsRouteConfig;
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(agentGroupId?: string): void {
    const agentGroupModel: IFormDialogData = {
      id: agentGroupId
    };
    this.isNewGroup = agentGroupId ? false : true;
    this.openDialog(AgentGroupDialogComponent, agentGroupModel);
  }

  private isAgentGroupResult(result: unknown): result is AgentGroup {
    if (typeof result !== 'object' || result === null) {
      return false;
    }

    const candidate = result as Partial<AgentGroup>;

    return typeof candidate.id === 'string'
      && typeof candidate.name === 'string'
      && typeof candidate.casId === 'string'
      && typeof candidate.organizationLevelId === 'string';
  }

  handleDialogClose(dialogRef: MatDialogRef<unknown, unknown>): void {
    if (!this.isNewGroup) {
      super.handleDialogClose(dialogRef);
      return;
    }
    dialogRef.afterClosed().subscribe({
      next: (result: unknown) => {
        if (this.isAgentGroupResult(result)) {
          this.isNewGroup = false;
          this.addAgentsToGroup(result);
          this.loadPage();
        }
      }
    });
  }

  showAgentGroupDetails(row: AgentGroup): void {
    this.openFlyoutInternal(AgentGroupDetailsPopupComponent, row.id, '80vw', true);
  }

  addAgentsToGroup(agentGroup: AgentGroup) {
    const agentGroupModel: IFormDialogData = { ...agentGroup };
    this.openDialog(GroupToAgentDialogComponent, agentGroupModel);
  }

  addAgentGroupToSite(id: string) {
    const data: AgentGroupOrgLevel = {
      agentGroupId: id,
      organizationLevelId: this.selectedOrg?.id
    };
    this.agentGroupOrgLevelService.create(data).subscribe({
      next: () => {
        this.loadComponentData();
        this.toastService.showSuccess('Agent Group assigned to Organization Level successfully', true);
      },
      error: (error) => {
        this.toastService.showError(error);
      }
    });
  }

  openRemoveAgentGroupFromSiteDialog(id: string) {
    const data: IDeleteDialogData = {
      id: id,
      formDeletes: true,
      customDelete: () => this.agentGroupOrgLevelService.delete(id, this.selectedOrg.id),
      title: this.translationService.instant('Agent Group from Site'),
      service: this.agentGroupOrgLevelService,
    }
    const dialogRef = this.dialog.open(DeleteDialog, {
      data: data,
      width: '440px',
      disableClose: true
    });
    this.handleDialogClose(dialogRef);
  }

  override openEntityDeleteDialog(agentGroup: AgentGroup): void {
    if (this.showArchivedOnly) {
      this.isLoading = true;
      this.agentGroupService.restore(agentGroup.id).subscribe({
        next: () => {
          this.toastService.showSuccess('Agent Group restored successfully', true);
          this.loadPage();
        },
        error: (error) => {
          this.toastService.showError(error);
          this.isLoading = false;
        }
      });

      return;
    }

    this.openDeleteDialog(DeleteDialog, agentGroup, this.agentGroupService, 'Archive Agent Group');
  }

  override loadPage(): void {
    if (!this.showArchivedOnly) {
      super.loadPage();
      return;
    }

    this.isLoading = true;
    this.agentGroupService.getDeletedList(
      this.searchField?.nativeElement?.value ?? '',
      this.sortActive || this.getDefaultOrderByColumn(),
      this.defaultOrderBy || 'asc',
      this.paginator?.pageIndex ?? 0,
      this.paginator?.pageSize ?? this.getPageSize(),
    ).subscribe({
      next: (result) => {
        this.dataSource.updateData(result.data);
        this.dataSource.setCount(result.count);
        if (this.paginator) {
          this.paginator.length = result.count;
        }
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  private toggleArchivedView(): void {
    this.showArchivedOnly = !this.showArchivedOnly;
    this.editDisabled = this.showArchivedOnly;
    this.deleteDisabled = this.showArchivedOnly;
    this.headerClassByColumn[nameOf<AgentGroup>(x => x.addedToMySite)] = this.showArchivedOnly ? 'd-none' : '';
    this.additionalActions = this.showArchivedOnly ? [
      {
        name: 'Restore',
        icon: 'fas fa-undo',
        action: (row: AgentGroup) => this.openEntityDeleteDialog(row)
      }
    ] : [];
    
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.clearRowSelection();
    this.additionalButtons = [
      {
        title: this.showArchivedOnly ? 'Show Active Agent Groups' : 'Show Archived Agent Groups',
        readonly: false,
        onClick: () => this.toggleArchivedView()
      }
    ];
    this.loadPage();
  }
}
